import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { X } from "@phosphor-icons/react";
import { useGlobalToast } from "components/GlobalToastProvider";
import useDocumentOverride from "hooks/api/documentGeneration/useDocumentOverride";
import type { ActiveDocumentOverride } from "hooks/api/documentGeneration/useGetDocumentChecklistStatus";
import { DOCUMENT_TYPES } from "./GenerateDocumentDialog/documentTypes";

export interface DocumentSettingsDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
  /** Client the override belongs to */
  clientId: string;
  /** Client's first name, shown in the subtitle */
  clientFirstName?: string;
  /** Client's last name — only the initial is shown */
  clientLastName?: string;
  /** Whether the override status is still loading */
  isLoading: boolean;
  /** The client's active override, or null when none is held */
  activeOverride: ActiveDocumentOverride | null;
}

const SWITCH_LABEL_ID = "document-settings-override-label";

const titleForType = (documentType: string | null) =>
  (documentType && DOCUMENT_TYPES[documentType]?.title) || "this document";

const buildClientPossessive = (firstName?: string, lastName?: string) => {
  const lastInitial = lastName?.trim().charAt(0) ?? "";
  const name = [firstName?.trim(), lastInitial].filter(Boolean).join(" ");
  return name || "this client";
};

/**
 * Lets a supervisor grant or withdraw the client's document generation
 * override. At most one is active per client, so the radio group is a
 * replacement: saving a different type discards the previous grant.
 *
 * @param props
 * @param props.open - Whether the dialog is open
 * @param props.onClose - Callback when the dialog should close
 * @param props.clientId - Client the override belongs to
 * @param props.clientFirstName - Client's first name, shown in the subtitle
 * @param props.clientLastName - Client's last name, shown as an initial
 * @param props.isLoading - Whether the override status is still loading
 * @param props.activeOverride - The client's active override, or null
 */
const DocumentSettingsDialog: React.FC<DocumentSettingsDialogProps> = ({
  open,
  onClose,
  clientId,
  clientFirstName,
  clientLastName,
  isLoading,
  activeOverride,
}) => {
  const { showToast } = useGlobalToast();

  const savedEnabled = Boolean(activeOverride);
  const savedType = activeOverride?.documentType ?? null;

  const [enabled, setEnabled] = useState(savedEnabled);
  const [documentType, setDocumentType] = useState<string | null>(savedType);

  useEffect(() => {
    if (open) {
      setEnabled(savedEnabled);
      setDocumentType(savedType);
    }
  }, [open, savedEnabled, savedType]);

  // Set only for a grant, so the toast fires on success and never on a 403.
  const pendingGrantRef = useRef<string | null>(null);

  const { grantOverride, revokeOverride, isSaving } = useDocumentOverride(clientId, {
    onSuccess: () => {
      showToast({
        message: pendingGrantRef.current
          ? `Override saved. ${titleForType(pendingGrantRef.current)} can be generated once without meeting all requirements.`
          : "Override Cancelled",
      });
      pendingGrantRef.current = null;
      onClose();
    },
    onError: () => {
      pendingGrantRef.current = null;
      showToast({ message: "Whoops! Something went wrong", errorState: true });
    },
  });

  const hasChanges = enabled !== savedEnabled || documentType !== savedType;
  const canSave = hasChanges && (!enabled || Boolean(documentType)) && !isSaving;

  const isReplacing = savedEnabled && enabled && Boolean(documentType) && documentType !== savedType;
  const isCancelling = savedEnabled && !enabled;

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    setDocumentType(checked ? savedType : null);
  };

  const handleSave = () => {
    if (enabled && documentType) {
      pendingGrantRef.current = documentType;
      grantOverride(documentType);
      return;
    }
    pendingGrantRef.current = null;
    revokeOverride();
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 11 }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Box>
          <Typography variant="title.medium" component="h2">
            Document Settings
          </Typography>
          <Typography variant="body.small" color="text.secondary">
            {`Manage upload and generation preferences for ${buildClientPossessive(clientFirstName, clientLastName)}'s documents.`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close dialog" size="small" disabled={isSaving}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pt: 0, pb: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={5} sx={{ mt: 5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body.base" id={SWITCH_LABEL_ID}>
              Allow generation with missing requirements
            </Typography>
            <Typography variant="body.small" color="text.secondary">
              {`Lets this client's clinician generate documents even if required information is missing or expired. All failed checks are skipped, not just one. Turns off after the document is signed and sent.`}
            </Typography>
          </Box>
          <Switch
            checked={enabled}
            onChange={(event) => handleToggle(event.target.checked)}
            disabled={isSaving}
            inputProps={{ "aria-labelledby": SWITCH_LABEL_ID }}
          />
        </Stack>

        {enabled && (
          <FormControl sx={{ mt: 5 }} disabled={isSaving}>
            <FormLabel>Document type</FormLabel>
            <RadioGroup
              value={documentType ?? ""}
              onChange={(event) => setDocumentType(event.target.value)}
            >
              {Object.values(DOCUMENT_TYPES).map((config) => (
                <FormControlLabel
                  key={config.key}
                  value={config.key}
                  control={<Radio />}
                  label={config.title}
                />
              ))}
            </RadioGroup>
            <FormHelperText>
              The override will clear after the document is generated and the first signature is
              submitted
            </FormHelperText>
          </FormControl>
        )}

        {isReplacing && (
          <Alert severity="warning" variant="standard" sx={{ mt: 5 }}>
            Saving replaces the current override for {titleForType(savedType)}. That document will
            need approval again.
          </Alert>
        )}
        {isCancelling && (
          <Alert severity="warning" variant="standard" sx={{ mt: 5 }}>
            Saving cancels the active override for {titleForType(savedType)}.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5 }}>
        <Button variant="contained" color="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSaving}
          disabled={!canSave}
          onClick={handleSave}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentSettingsDialog;
